import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  DollarSign,
  Activity,
  Brain,
  Key,
  Database,
  Package,
  Store,
  BarChart3,
  Ship,
  ContactRound,
  CheckSquare,
  Users,
  Settings,
  Code2,
  Target,
  BookOpen,
  GraduationCap,
  Search,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { pedirSalida } from '@/lib/salida-con-cambios';

interface SearchItem {
  label: string;
  description: string;
  href: string;
  icon: any;
  section: string;
  keywords: string[];
}

const SEARCH_ITEMS: SearchItem[] = [
  // General
  {
    label: 'Inicio',
    description: 'Resumen de KPIs y cotizaciones',
    href: '/dashboard',
    icon: LayoutDashboard,
    section: 'General',
    keywords: ['inicio', 'dashboard', 'home', 'kpi', 'resumen'],
  },
  // Datos
  {
    label: 'API Keys',
    description: 'Gestionar claves de integración',
    href: '/claves-api',
    icon: Key,
    section: 'Datos',
    keywords: ['api', 'key', 'clave', 'integracion', 'token'],
  },
  {
    label: 'Datos Maestros',
    description: 'Divisas, LOBs, impuestos',
    href: '/master-data',
    icon: Database,
    section: 'Datos',
    keywords: ['divisa', 'moneda', 'lob', 'impuesto', 'maestro'],
  },
  {
    label: 'Materiales',
    description: 'Catálogo de materiales y productos',
    href: '/materials',
    icon: Package,
    section: 'Datos',
    keywords: ['material', 'catalogo', 'producto', 'item'],
  },
  // Pricing
  {
    label: 'Modelos de Pricing',
    description: 'Configurar modelos con variables y fórmulas',
    href: '/pricing-models',
    icon: DollarSign,
    section: 'Cotiza',
    keywords: ['modelo', 'formula', 'variable', 'precio', 'pricing'],
  },
  {
    label: 'Monitoreo',
    description: 'Tus precios contra cada tienda y qué cambió en la competencia',
    href: '/compite/monitoreo',
    icon: Activity,
    section: 'Compite',
    keywords: ['monitoreo', 'competencia', 'cambios', 'lecturas', 'tiendas', 'comparar'],
  },
  {
    label: 'Productos',
    description: 'Tu catálogo, el de cada tienda y los vínculos',
    href: '/compite/productos',
    icon: Package,
    section: 'Compite',
    keywords: [
      'productos',
      'catalogo',
      'vincular',
      'inferencia',
      'match',
      'importar',
      'csv',
      'vinculos',
    ],
  },
  {
    label: 'Competencia',
    description: 'Las tiendas contra las que te comparas y cuándo se leen',
    href: '/compite/competencia',
    icon: Store,
    section: 'Compite',
    keywords: [
      'competencia',
      'competidor',
      'tienda',
      'lectura',
      'scraping',
      'frecuencia',
      'precios dinamicos',
      'dinamico',
    ],
  },
  {
    label: 'Analítica',
    description: 'Evolución de tus precios y los de la competencia',
    href: '/compite/analitica',
    icon: BarChart3,
    section: 'Compite',
    keywords: ['grafico', 'evolucion', 'brecha', 'posicionamiento', 'competencia'],
  },
  // Rediseño del 14-oct: Estrategias de Mercado se partió en Decisiones (día a
  // día) y Estrategia (configuración). Las palabras de antes siguen encontrando
  // lo mismo: «estrategia», «regla», «margen mínimo».
  {
    label: 'Decisiones',
    description: 'Los precios que esperan tu sí y cuánta plata hay en juego',
    href: '/compite/decisiones',
    icon: Brain,
    section: 'Compite',
    keywords: [
      'decisiones',
      'decidir',
      'por decidir',
      'aprobar',
      'propuesta',
      'revisar precios',
      'hoy',
      'estrategias de mercado',
      'inteligente',
      'smart',
    ],
  },
  {
    label: 'Estrategia',
    description: 'Estrategias, reglas y cómo queda cada producto',
    href: '/compite/estrategia',
    icon: Brain,
    section: 'Compite',
    keywords: [
      'estrategia',
      'mercado',
      'inteligente',
      'recomendacion',
      'proyeccion',
      'cpi',
      // Reglas vive adentro desde el plan 3 (épica H): /pricing-rules redirige acá.
      'regla',
      'límite',
      'margen mínimo',
    ],
  },
  {
    label: 'Analítica',
    description: 'Márgenes, tendencias y rendimiento',
    href: '/analytics',
    icon: BarChart3,
    section: 'Cotiza',
    keywords: ['analitica', 'grafico', 'reporte', 'margen', 'tendencia'],
  },
  {
    label: 'Inteligencia de Pricing',
    description: 'Simulaciones y predicciones con IA',
    href: '/ai-intelligence',
    icon: Brain,
    section: 'Cotiza',
    keywords: ['ia', 'prediccion', 'simulacion', 'inteligencia'],
  },
  // Comercial
  {
    label: 'Cotizaciones',
    description: 'Listado de cotizaciones',
    href: '/quotes',
    icon: FileText,
    section: 'Cotiza',
    keywords: ['cotizacion', 'quote', 'presupuesto', 'propuesta'],
  },
  {
    label: 'Nueva Cotización',
    description: 'Crear cotización desde cero',
    href: '/quotes/new',
    icon: FileText,
    section: 'Cotiza',
    keywords: ['nueva', 'crear', 'cotizacion', 'quote'],
  },
  {
    label: 'Clientes',
    description: 'Gestionar clientes y contactos',
    href: '/customers',
    icon: ContactRound,
    section: 'Cotiza',
    keywords: ['cliente', 'contacto', 'empresa', 'customer'],
  },
  {
    label: 'Aprobaciones',
    description: 'Aprobar o rechazar cotizaciones',
    href: '/approvals',
    icon: CheckSquare,
    section: 'Cotiza',
    keywords: ['aprobacion', 'aprobar', 'rechazar', 'pendiente'],
  },
  {
    label: 'Comercia',
    description: 'Producto, logística y costo nacionalizado de tu importación',
    href: '/comercia/productos',
    icon: Ship,
    section: 'Comercia',
    keywords: [
      'importacion',
      'contenedor',
      'proveedor',
      'flete',
      'ruta',
      'costo nacionalizado',
      'proforma',
      'partida',
      'trade',
    ],
  },
  // Sistema
  {
    label: 'Mi empresa',
    description: 'Logo, apariencia, equipos, documentos y facturación',
    href: '/mi-empresa',
    icon: Users,
    section: 'Sistema',
    keywords: [
      'empresa',
      'logo',
      'equipo',
      'plantilla',
      'documento',
      'factura',
      'pago',
      'aprobacion',
    ],
  },
  {
    label: 'Usuarios',
    description: 'Gestionar usuarios y roles',
    href: '/mi-empresa/usuarios',
    icon: Users,
    section: 'Sistema',
    keywords: ['usuario', 'rol', 'permiso', 'acceso', 'invitar'],
  },
  {
    label: 'Configuración',
    description: 'Ajustes generales del sistema',
    href: '/settings',
    icon: Settings,
    section: 'Sistema',
    keywords: ['configuracion', 'ajuste', 'setting', 'preferencia'],
  },
  {
    label: 'API Developers',
    description: 'Documentación de APIs REST',
    href: '/developer',
    icon: Code2,
    section: 'Sistema',
    keywords: ['api', 'developer', 'documentacion', 'rest', 'endpoint'],
  },
  {
    label: 'Estado Proyecto',
    description: 'Progreso de implementación',
    href: '/project-status',
    icon: Target,
    section: 'Sistema',
    keywords: ['estado', 'progreso', 'proyecto', 'status'],
  },
  {
    label: 'Centro de Ayuda',
    description: 'Manual, FAQ e inicio rápido',
    href: '/help',
    icon: BookOpen,
    section: 'Sistema',
    keywords: ['ayuda', 'help', 'faq', 'manual', 'soporte'],
  },
  {
    label: 'Tutorial',
    description: 'Guía paso a paso',
    href: '/tutorial',
    icon: GraduationCap,
    section: 'Sistema',
    keywords: ['tutorial', 'guia', 'aprender', 'paso'],
  },
];

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Filter results
  const results = useMemo(() => {
    if (!query.trim()) return SEARCH_ITEMS;
    const q = query
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return SEARCH_ITEMS.filter((item) => {
      const haystack = [item.label, item.description, ...item.keywords]
        .join(' ')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      return haystack.includes(q);
    });
  }, [query]);

  // Group by section
  const grouped = useMemo(() => {
    const groups: Record<string, SearchItem[]> = {};
    for (const item of results) {
      if (!groups[item.section]) groups[item.section] = [];
      groups[item.section].push(item);
    }
    return groups;
  }, [results]);

  // Flat list for keyboard navigation
  const flatResults = useMemo(() => results, [results]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = (href: string) => {
    setOpen(false);
    setQuery('');
    // Spec 294: con cambios propios sin aplicar en Reglas, salir pregunta antes.
    pedirSalida(() => navigate(href));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(flatResults[selectedIndex].href);
    }
  };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        /*
          El disparador del buscador SÍ es una superficie: es una pieza chica y
          contenida de la barra superior, y se hunde como el botón base.
          La transición nombra lo que cambia; con `all` se animaba también el
          ancho, que se recalcula cuando aparece o desaparece el texto en `md`.
        */
        className="group flex h-10 items-center gap-2.5 rounded-full border border-border bg-muted/40 px-3.5 text-sm text-muted-foreground transition-[background-color,border-color,color,transform] duration-snap ease-snap hover:bg-muted/70 hover:text-foreground active:scale-[.98] md:w-72"
        aria-label="Buscar (Ctrl K)"
      >
        <Search className="h-4 w-4 shrink-0 opacity-70 transition-opacity group-hover:opacity-100" />
        <span className="hidden flex-1 text-left md:inline">Buscar</span>
        <kbd className="hidden rounded-md border bg-card px-1.5 py-0.5 font-sans text-2xs text-muted-foreground md:inline">
          Ctrl K
        </kbd>
      </button>

      {/* Search dialog */}
      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setQuery('');
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          className="sm:max-w-lg p-0 gap-0 overflow-hidden"
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b">
            <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <Input
              ref={inputRef}
              placeholder="Buscar páginas, funciones..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="border-0 shadow-none focus-visible:ring-0 h-12 text-sm"
              autoFocus
            />
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[360px] overflow-y-auto p-2">
            {flatResults.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No se encontraron resultados para "{query}"
              </div>
            ) : (
              Object.entries(grouped).map(([section, items]) => (
                <div key={section}>
                  <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                    {section}
                  </div>
                  {items.map((item) => {
                    const globalIndex = flatResults.indexOf(item);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.href}
                        data-index={globalIndex}
                        /*
                          Fila de resultado: responde con FONDO, no con escala.
                          Es navegación de ancho completo, y además el teclado la
                          recorre con `selectedIndex` — una escala pelearía con
                          ese resaltado en vez de acompañarlo.
                        */
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm transition-colors duration-snap ease-snap ${
                          globalIndex === selectedIndex
                            ? 'bg-primary text-primary-foreground active:bg-primary/90'
                            : 'hover:bg-accent active:bg-accent/70'
                        }`}
                        onClick={() => handleSelect(item.href)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0 opacity-70" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{item.label}</div>
                          <div
                            className={`text-xs truncate ${
                              globalIndex === selectedIndex
                                ? 'text-primary-foreground/70'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {item.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 border-t text-[11px] text-muted-foreground flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">↑↓</kbd> navegar
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">Enter</kbd> ir
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-muted border text-[10px]">Esc</kbd> cerrar
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
